import { useCallback, useMemo } from 'react'
import { useActiveEnvironment } from './useEnvironments'
import { useCollection } from '../resources/hooks/useCollection'

export type EntitlementKey =
  | 'snapshots'
  | 'limitPdv'
  | 'limitFiles'
  | 'largeFilesEnabled'
  | 'whitelabeling'
  | 'projectsEnabled'
  | 'versionHistory'
  | 'dataRetention'
  | 'aiAssist'
  | 'billingEnabled'
  | 'manageUsers'
  | 'googleDrive'
  | 'box'
  | 'pylon'
  | 'savedViews'
  | 'headerSelection'
  | 'portalEnabled'
  | 'customAppsEnabled'
  | 'autobuild'
  | 'dataClipsEnabled'
  | 'calculations'
  | 'paygEnabled'
  | 'mixpanelAnalytics'
  | 'flatfileOsProject'
  | 'trialEnabled'
  | 'autobuildV2'

interface Entitlement {
  key: EntitlementKey
  metadata?: Record<string, unknown>
}

export const useEntitlements = () => {
  const { environment, hasEnvAccess } = useActiveEnvironment()

  // Memoize the environment ID to prevent unnecessary collection refreshes
  const envId = useMemo(() => (hasEnvAccess ? environment?.id : undefined), [hasEnvAccess, environment?.id])

  const entitlements = useCollection<'ENTITLEMENTS'>('ENTITLEMENTS', { resourceId: envId }) as Entitlement[]

  // Memoize the hasEntitlement callback with the memoized entitlements
  const hasEntitlement = useCallback(
    (key: EntitlementKey) => {
      return entitlements.some((ent) => ent.key === key)
    },
    [entitlements]
  )

  const getEntitlementMetadata = useCallback(
    (key: EntitlementKey) => {
      const meta = entitlements.find((ent) => ent.key === key)?.metadata
      if (meta) {
        return meta as Record<EntitlementKey, any>
      }
      return null
    },
    [entitlements]
  )

  return {
    hasEntitlement,
    getEntitlementMetadata,
  }
}
